import get from "lodash/get"
import camelCase from "lodash/camelCase"
import { createRef, Component } from "react"
import PropTypes from "prop-types"
import { ReactSVG } from "react-svg"
import getContentUrl from "utils-lib/getContentUrl"
import joinClassNames from "utils-lib/joinClassNames"
import "./TextInput.scss"

export default class TextInput extends Component {
  constructor(props) {
    super(props)
    this.state = {
      visited: false,
      errorMessage: ``,
    }

    this.inputRef = createRef()
  }

  componentDidMount() {
    this.inputRef.current.addEventListener(`change`, this.onChange)

    this.inputInterval = setInterval(this.pollInput, 1000)
  }

  componentWillUnmount() {
    this.inputRef.current.removeEventListener(`change`, this.onChange)

    clearInterval(this.inputInterval)
  }

  componentDidUpdate(prevProps) {
    if (this.state.visited && prevProps.language !== this.props.language) {
      this.checkError(this.props.value)
    }
  }

  pollInput = () => {
    if (get(this.inputRef, `current.value`, ``)) {
      if (this.inputRef.current.value !== this.props.value) {
        this.onChange({ target: this.inputRef.current })
      }
    }
  }

  checkError = (value) => {
    const errorMessage = this.props.getErrorMessage(value)

    this.setState({
      errorMessage: this.props.getErrorMessage(value),
    })
    this.props.onValidate(errorMessage === ``)
  }

  onClick() {
    this.props.onButtonClick()
  }

  onChange = (e) => {
    this.props.onChange(e.target.value)
    if (this.state.visited) {
      this.checkError(e.target.value)
    }
  }

  renderFieldButton() {
    const {
      buttonChildren,
      buttonStyle,
      buttonTabIndex,
      customErrorMessage,
      disableValidation,
      isButtonChildrenAButton,
    } = this.props

    if (isButtonChildrenAButton) {
      return (
        <button
          data-testid="TextInput - button"
          className="btn-right btn-right-ie maax-font-weight-regular"
          style={{
            ...buttonStyle,
            position: `relative`,
            left:
              !disableValidation &&
              (this.state.errorMessage || customErrorMessage)
                ? `-1.5rem`
                : 0,
            top: `-.4rem`,
            transition: `.3s left`,
          }}
          tabIndex={buttonTabIndex}
          onClick={() => {
            this.onClick()
          }}
          type="button"
        >
          {buttonChildren}
        </button>
      )
    }

    /* The element to show on the right of the text input field is not a button. */
    return (
      <div
        className="btn-right pr-2 maax-font-weight-regular"
        style={{
          ...buttonStyle,
          position: `relative`,
          left:
            !disableValidation &&
            (this.state.errorMessage || customErrorMessage)
              ? `-1.5rem`
              : 0,
          top: `-.4rem`,
          transition: `.3s left`,
        }}
        tabIndex={buttonTabIndex}
      >
        {buttonChildren}
      </div>
    )
  }

  render() {
    const {
      buttonChildren,
      className,
      customErrorMessage,
      inputClassName,
      inputStyle,
      isControlled,
      isEmailField,
      isPasswordField,
      label,
      labelStyle,
      readOnly,
      showPassword,
      style,
      type,
      value,
      inputMaxLength,
      disableValidation,
      enableFloatLabel,
      hidePlaceholder,
      isTextarea,
      hideNoError,
    } = this.props

    const { errorMessage } = this.state

    const finalErrorMessage = customErrorMessage || errorMessage

    let inputClassNames = joinClassNames([
      `inputMaterial`,
      errorMessage || customErrorMessage ? `error` : null,
      isPasswordField ? `password` : null,
    ])

    if (inputClassName !== ``) {
      inputClassNames += ` ${inputClassName}`
    }

    const classNames = `TextInput${className !== `` ? ` ${className}` : ``}`

    let inputType = `input`
    if (isPasswordField && !showPassword) {
      inputType = `password`
    } else if (isEmailField) {
      inputType = `email`
    }

    const maxLength = inputMaxLength !== `` ? inputMaxLength : ``

    const handlerProps = readOnly
      ? {}
      : {
          onChange: this.onChange,
          onBlur: (e) => {
            /* Mark field as visited. */
            this.setState({
              visited: true,
            })
            this.props.onBlur(e.target.value)
            this.checkError(e.target.value)
          },
          onInput: (e) => {
            if (errorMessage !== ``) {
              this.checkError(e.target.value)
            }
          },
        }

    const inputProps = {
      className: inputClassNames,
      type: type || inputType,
      readOnly,
      maxLength,
      ...handlerProps,
    }

    /* Don't set the value for password fields to prevent the password displaying in the browser inspect tool. */
    /* Makes email input uncontrolled when not remembered to avoid overriding iOS autofill */
    if (isControlled) {
      inputProps.value = value
    }

    const InputComponent = isTextarea ? `textarea` : `input`

    return (
      <div className={classNames} style={style} data-testid="TextInput">
        <InputComponent
          ref={this.inputRef}
          {...inputProps}
          // for autocomplete
          placeholder={hidePlaceholder ? `` : label}
          id={camelCase(label)}
          style={inputStyle}
          aria-describedby={`error_${camelCase(label)}`}
        />
        {enableFloatLabel ? (
          <label
            className={value.length > 0 ? `focused-label` : ``}
            style={labelStyle}
            htmlFor={camelCase(label)}
          >
            {label}
          </label>
        ) : null}
        {buttonChildren !== null ? this.renderFieldButton() : null}
        {disableValidation ? null : (
          <div
            className="pt-2"
            style={{
              minHeight:
                hideNoError && !finalErrorMessage ? `.25rem` : `2.5rem`,
            }}
          >
            {finalErrorMessage ? (
              <ReactSVG
                wrapper="span"
                src={getContentUrl("/content/dam/wm/icons/red-alert-icon.svg")}
                style={{
                  position: `absolute`,
                  right: `.625rem`,
                  top: `.8rem`,
                }}
              />
            ) : null}
            <div className="validation-error" id={`error_${camelCase(label)}`}>
              {finalErrorMessage}
            </div>
          </div>
        )}
      </div>
    )
  }
}

TextInput.propTypes = {
  /* Required props. */
  value: PropTypes.string.isRequired,

  /* Optional props. */
  disableValidation: PropTypes.bool,
  label: PropTypes.string,
  buttonChildren: PropTypes.node,
  buttonStyle: PropTypes.object,
  buttonTabIndex: PropTypes.number,
  className: PropTypes.string,
  customErrorMessage: PropTypes.string,
  getErrorMessage: PropTypes.func,
  inputClassName: PropTypes.string,
  inputStyle: PropTypes.object,
  isControlled: PropTypes.bool,
  isEmailField: PropTypes.bool,
  isPasswordField: PropTypes.bool,
  labelClassName: PropTypes.string,
  labelStyle: PropTypes.object,
  language: PropTypes.string,
  onButtonClick: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onValidate: PropTypes.func,
  readOnly: PropTypes.bool,
  showPassword: PropTypes.bool,
  style: PropTypes.object,
  type: PropTypes.string,
  inputMaxLength: PropTypes.string,
  enableFloatLabel: PropTypes.bool,
  isButtonChildrenAButton: PropTypes.bool,
  inputID: PropTypes.string,
  hidePlaceholder: PropTypes.bool,
  isTextarea: PropTypes.bool,
  hideNoError: PropTypes.bool,
}

TextInput.defaultProps = {
  buttonChildren: null,
  buttonStyle: {},
  buttonTabIndex: 0,
  isButtonChildrenAButton: true,
  className: ``,
  disableValidation: false,
  getErrorMessage: () => {},
  inputClassName: ``,
  inputStyle: {},
  isControlled: true,
  isEmailField: false,
  isPasswordField: false,
  labelClassName: ``,
  labelStyle: {},
  language: ``,
  onButtonClick: () => {},
  onBlur: () => {},
  onChange: () => {},
  onValidate: () => {},
  readOnly: false,
  showPassword: false,
  style: {},
  inputMaxLength: ``,
  enableFloatLabel: true,
  inputID: ``,
  hidePlaceholder: false,
  isTextarea: false,
  type: ``,
  hideNoError: false,
  customErrorMessage: ``,
  label: ``,
}
